import { useState } from "react";
import DownloadCsvTemplate from "./DownloadCsvTemplate";

const normalizeHeader = (value) =>
  value
    .trim()
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
const truthy = ["true", "1", "yes", "y"];
const falsy = ["", "false", "0", "no", "n"];

const parseCsvLine = (line) => {
  const values = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];
    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === "," && !inQuotes) {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current.trim());
  return values;
};

const isDriverValue = (value) =>
  truthy.includes(
    String(value || "")
      .trim()
      .toLowerCase(),
  );
const isValidBoolean = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return truthy.includes(normalized) || falsy.includes(normalized);
};
const isPositiveInteger = (value) =>
  /^[1-9][0-9]*$/.test(String(value || "").trim());

const UploadModal = ({ isOpen, onClose, onUpload, isRSVPOnly }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState([]);
  const [uploadError, setUploadError] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const validateFileMeta = (file) => {
    if (!file) return ["Please select a CSV file."];
    if (!file.name.toLowerCase().endsWith(".csv")) {
      return ["Please upload a .csv file."];
    }
    if (file.size > 5 * 1024 * 1024) {
      return ["File size should not exceed 5MB."];
    }
    return [];
  };

  const buildPreview = (text) => {
    const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
    if (lines.length < 2) {
      return {
        errors: ["CSV must include a header row and at least one person."],
        rows: [],
      };
    }

    const headers = parseCsvLine(lines[0]).map(normalizeHeader);
    const missing = ["firstname", "phone"].filter(
      (header) => !headers.includes(header),
    );
    if (missing.length) {
      return {
        errors: [`Missing required column(s): ${missing.join(", ")}.`],
        rows: [],
      };
    }

    const rows = [];
    const rowErrors = [];
    lines.slice(1).forEach((line, index) => {
      const values = parseCsvLine(line);
      const row = headers.reduce((acc, header, headerIndex) => {
        acc[header] = values[headerIndex] || "";
        return acc;
      }, {});
      const rowNumber = index + 2;
      const driver = !isRSVPOnly && isDriverValue(row.isdriver);
      const prefix = `Row ${rowNumber} (${row.firstname || "Unnamed"})`;

      if (!row.firstname?.trim())
        rowErrors.push(`${prefix}: firstName is required.`);
      if (!row.phone?.trim()) rowErrors.push(`${prefix}: phone is required.`);
      if (!isValidBoolean(row.isdriver)) {
        rowErrors.push(
          `${prefix}: isDriver must be true/false, yes/no, or 1/0.`,
        );
      }
      if (row.noofppl && !isPositiveInteger(row.noofppl)) {
        rowErrors.push(
          `${prefix}: noOfPpl must be a whole number greater than 0.`,
        );
      }
      if (!isRSVPOnly && driver && !row.cantake?.trim()) {
        rowErrors.push(`${prefix}: canTake is required when isDriver is TRUE.`);
      }
      if (
        !isRSVPOnly &&
        driver &&
        row.cantake &&
        !isPositiveInteger(row.cantake)
      ) {
        rowErrors.push(
          `${prefix}: canTake must be a whole number greater than 0.`,
        );
      }
      if (!isRSVPOnly && !driver && !row.ridelocation?.trim()) {
        rowErrors.push(`${prefix}: rideLocation is required for non-drivers.`);
      }

      rows.push({
        rowNumber,
        firstName: row.firstname || "",
        lastName: row.lastname || "",
        phone: row.phone || "",
        rideLocation: row.ridelocation || "",
        isDriver: driver,
        canTake: row.cantake || "",
        noOfPpl: row.noofppl || "1",
      });
    });

    return { errors: rowErrors, rows };
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file || null);
    setPreview(null);
    setErrors([]);
    setUploadError("");

    const metaErrors = validateFileMeta(file);
    if (metaErrors.length) {
      setErrors(metaErrors);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = buildPreview(event.target.result || "");
      setPreview(result.rows);
      setErrors(result.errors);
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    const metaErrors = validateFileMeta(selectedFile);
    if (metaErrors.length || errors.length) {
      setErrors(metaErrors.length ? metaErrors : errors);
      return;
    }

    setIsUploading(true);
    setUploadError("");
    const result = await onUpload(selectedFile);
    setIsUploading(false);

    if (result?.ok === false) {
      setUploadError(
        result.message || "Upload failed. Please review the CSV and try again.",
      );
      return;
    }
    onClose();
  };

  if (!isOpen) return null;

  const participants = (preview || []).filter((row) => !row.isDriver);
  const drivers = (preview || []).filter((row) => row.isDriver);
  const canUpload =
    selectedFile && preview?.length > 0 && errors.length === 0 && !isUploading;

  return (
    <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-900 bg-opacity-70 px-3 py-6">
      <div className="mx-auto w-full max-w-5xl rounded-lg bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h4 className="text-xl font-black text-gray-900">
              Upload Spreadsheet
            </h4>
            <p className="mt-1 text-sm text-gray-500">
              Required columns: firstName and phone. For ride events, drivers
              need canTake and non-drivers need rideLocation.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <DownloadCsvTemplate isRSVPOnly={isRSVPOnly} />
          <input
            type="file"
            onChange={handleFileChange}
            accept=".csv"
            className="file-input w-full max-w-md"
          />
        </div>

        {(errors.length > 0 || uploadError) && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <p className="font-bold">
              Please fix the following before uploading:
            </p>
            {uploadError && <p className="mt-2">{uploadError}</p>}
            {errors.length > 0 && (
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {preview && errors.length === 0 && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-semibold text-green-800">
            Looks good: {participants.length} participant(s) and{" "}
            {drivers.length} driver(s) ready to upload.
          </div>
        )}

        {preview && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <PreviewTable
              title="Participants"
              rows={participants}
              showDriverFields={false}
            />
            {!isRSVPOnly && (
              <PreviewTable title="Drivers" rows={drivers} showDriverFields />
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!canUpload}
            className="rounded-lg bg-[#004a7e] px-4 py-2 text-sm font-bold text-white hover:bg-[#003a64] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isUploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

const PreviewTable = ({ title, rows, showDriverFields }) => (
  <div className="overflow-hidden rounded-lg border border-gray-200">
    <div className="bg-gray-50 px-3 py-2 text-sm font-black text-gray-900">
      {title} ({rows.length})
    </div>
    <div className="max-h-72 overflow-auto">
      <table className="min-w-full text-left text-xs">
        <thead className="sticky top-0 bg-white text-gray-500">
          <tr>
            <th className="px-3 py-2">Row</th>
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Phone</th>
            {showDriverFields ? (
              <th className="px-3 py-2">Can Take</th>
            ) : (
              <th className="px-3 py-2">Ride Location</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.length === 0 ? (
            <tr>
              <td className="px-3 py-3 text-gray-500" colSpan="4">
                No rows.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.rowNumber}>
                <td className="px-3 py-2 text-gray-500">{row.rowNumber}</td>
                <td className="px-3 py-2 font-semibold">
                  {row.firstName} {row.lastName}
                </td>
                <td className="px-3 py-2">{row.phone}</td>
                <td className="px-3 py-2">
                  {showDriverFields ? row.canTake : row.rideLocation}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default UploadModal;
